import {
  DriveFolderRef,
  DriveTreeNode,
  ResourceFilter,
  SyllabusTreeNode,
} from './drive.types';

export function getResourceCoverUrl(
  cover: string | null | undefined,
  protocol: string,
  host: string,
  bucket: string
): string {
  if (!cover) {
    return '';
  }
  if (cover.startsWith('http')) {
    return cover;
  }
  const bucketPath = bucket ? `${bucket}/` : '';
  return `${protocol}//${host}/${bucketPath}${cover}`;
}

export function getChildrenAtPath(
  syllabus: SyllabusTreeNode[],
  pathIds: number[]
): SyllabusTreeNode[] {
  let nodes = syllabus;
  for (const id of pathIds) {
    const node = nodes.find(n => n.id === id);
    if (!node) {
      return [];
    }
    nodes = node.children ?? [];
  }
  return nodes;
}

export function hasActiveThemes(themes: Record<string, unknown> | undefined): boolean {
  if (!themes) {
    return false;
  }
  return Object.values(themes).some(v => v !== '' && v !== null && v !== undefined);
}

export function syncFilterFromPathIds(
  filter: ResourceFilter,
  pathIds: Array<number | string>
): ResourceFilter {
  const ids = pathIds.map(id => Number(id)).filter(n => !Number.isNaN(n));
  const synced: ResourceFilter = {
    ...filter,
    themes: { ...(filter.themes || {}) },
    level_filters: { ...(filter.level_filters || {}) },
  };

  if (ids.length >= 1) {
    synced.syllabus_id = ids[0];
  }
  if (ids.length >= 2) {
    synced.course_id = ids[1];
  }
  if (ids.length > 2) {
    synced.level_filters = { ...(synced.level_filters || {}) };
    for (let i = 2; i < ids.length; i++) {
      synced.level_filters[`level_${i}`] = ids[i];
    }
  }
  synced.path = `/${ids.join('/')}/`;
  return synced;
}

export function buildDriveQuery(
  filter: ResourceFilter,
  pathIds: number[]
): Record<string, unknown> {
  const hasSearch = !!filter.name?.trim();
  const base: Record<string, unknown> = {
    pageIndex: filter.pageIndex,
    pageSize: filter.pageSize,
    name: filter.name || undefined,
  };

  if (filter.reserve3 !== '' && filter.reserve3 !== null && filter.reserve3 !== undefined) {
    base['reserve3'] = filter.reserve3;
  }
  if (hasActiveThemes(filter.themes)) {
    base['themes'] = { ...(filter.themes || {}) };
  }

  if (hasSearch) {
    return base;
  }

  if (pathIds.length > 0) {
    base['folder_id'] = pathIds[pathIds.length - 1];
  }

  return base;
}

export function resolveDriveFolderRef(
  folders: DriveFolderRef[] | undefined,
  preferredFolderId?: number | null,
): DriveFolderRef | null {
  const list = folders ?? [];
  if (!list.length) {
    return null;
  }
  if (preferredFolderId != null) {
    return list.find((folder) => folder.folderId === preferredFolderId) ?? null;
  }
  return list[0];
}

export function resolveDriveFolderId(
  folders: DriveFolderRef[] | undefined,
  preferredFolderId?: number | null,
): number | null {
  return resolveDriveFolderRef(folders, preferredFolderId)?.folderId ?? null;
}

export function parseDrivePathParam(pathParam: string | null | undefined): number[] {
  if (!pathParam?.trim()) {
    return [];
  }
  return pathParam
    .split('/')
    .filter(Boolean)
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id) && id > 0);
}

export function validatePathIdsInSyllabus(
  syllabus: SyllabusTreeNode[],
  pathIds: number[]
): number[] {
  const valid: number[] = [];
  let nodes = syllabus ?? [];
  for (const id of pathIds) {
    const node = nodes.find((n) => n.id === id);
    if (!node) {
      break;
    }
    valid.push(id);
    nodes = node.children ?? [];
  }
  return valid;
}

export function buildPathIdsFromFolder(
  folderId: number | null | undefined,
  folderPath: string | null | undefined,
  nodeLookup?: Map<number, SyllabusTreeNode>
): number[] {
  if (!folderId) {
    return [];
  }
  if (folderPath) {
    const ids = folderPath
      .split('/')
      .filter(Boolean)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
    if (!ids.includes(folderId)) {
      ids.push(folderId);
    }
    return ids;
  }
  if (nodeLookup) {
    const path: number[] = [];
    let currentId: number | undefined = folderId;
    while (currentId) {
      const node = nodeLookup.get(currentId);
      if (!node) {
        break;
      }
      path.unshift(node.id);
      if (!node.pid) {
        break;
      }
      currentId = node.pid;
    }
    return path.length ? path : [folderId];
  }
  return [folderId];
}

export function syllabusToNzTree(
  nodes: SyllabusTreeNode[],
  ancestorIds: number[] = []
): DriveTreeNode[] {
  return (nodes || []).map(n => {
    const pathIds = [...ancestorIds, n.id];
    return {
      key: String(n.id),
      title: n.name,
      isLeaf: !n.children?.length,
      pathIds,
      origin: n,
      children: n.children?.length ? syllabusToNzTree(n.children, pathIds) : undefined,
    };
  });
}

export function collectDescendantIds(
  rootId: number,
  syllabus: SyllabusTreeNode[],
): number[] {
  const findNode = (nodes: SyllabusTreeNode[], id: number): SyllabusTreeNode | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children?.length) {
        const found = findNode(node.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };
  const collect = (node: SyllabusTreeNode): number[] => {
    const ids: number[] = [];
    (node.children ?? []).forEach((child) => {
      ids.push(child.id);
      ids.push(...collect(child));
    });
    return ids;
  };
  const root = findNode(syllabus, rootId);
  return root ? collect(root) : [];
}

export function buildMoveTargetTreeNodes(
  nodes: DriveTreeNode[],
  disabledIds: Set<number>,
): DriveTreeNode[] {
  return (nodes || []).map((node) => ({
    ...node,
    disabled: disabledIds.has(+node.key),
    children: node.children?.length
      ? buildMoveTargetTreeNodes(node.children as DriveTreeNode[], disabledIds)
      : undefined,
  }));
}
